import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Euro, Users, AlertCircle, CheckCircle, Loader2, Gift, Percent, RefreshCw, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const CommissionSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [recalculateResult, setRecalculateResult] = useState(null);
  
  const [tiers, setTiers] = useState([
    { name: 'Palier 1', min_sales: 0, max_sales: 9, commission: 10 },
    { name: 'Palier 2', min_sales: 10, max_sales: 19, commission: 15 },
    { name: 'Palier 3', min_sales: 20, max_sales: 39, commission: 20 },
    { name: 'Palier 4', min_sales: 40, max_sales: 999, commission: 25 },
  ]);
  
  const [referral, setReferral] = useState({
    n1_commission: 2,
    n2_commission: 1
  });

  const [clientReferral, setClientReferral] = useState({
    vendor_commission_on_client_referral: 2,  // Commission vendeur quand son client parraine
    referred_client_discount: 5,              // Réduction pour le client parrainé (en €)
    client_referrer_bonus: 10                 // Avoir pour le client parrain (en €)
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/commission-settings');
      if (response.data.tiers) {
        setTiers(response.data.tiers);
      }
      if (response.data.referral) {
        setReferral(response.data.referral);
      }
      if (response.data.client_referral) {
        setClientReferral(response.data.client_referral);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await api.put('/admin/commission-settings', {
        tiers,
        referral,
        client_referral: clientReferral
      });
      setSuccess('Paramètres enregistrés avec succès !');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculateTiers = async () => {
    setRecalculating(true);
    setError('');
    setRecalculateResult(null);
    
    try {
      const response = await api.post('/admin/recalculate-vendor-tiers');
      setRecalculateResult(response.data);
      setSuccess('Paliers recalculés avec succès !');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du recalcul');
    } finally {
      setRecalculating(false);
    }
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index][field] = field === 'name' ? value : parseFloat(value) || 0;
    setTiers(newTiers);
  };

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    setTiers([...tiers, {
      name: `Palier ${tiers.length + 1}`,
      min_sales: (lastTier?.max_sales || 0) + 1,
      max_sales: (lastTier?.max_sales || 0) + 10,
      commission: (lastTier?.commission || 10) + 5
    }]);
  };

  const removeTier = (index) => {
    if (tiers.length <= 1) return;
    setTiers(tiers.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="commission-settings">
      {/* Messages */}
      {error && (
        <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 text-[#FF3B5C] p-4 rounded-lg flex items-center space-x-3">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 text-[#00FF88] p-4 rounded-lg flex items-center space-x-3">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Commission Tiers */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#1A1A1D] p-6 rounded-lg metallic-border"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Euro className="h-6 w-6 text-[#00FFFF]" />
            <h3 className="text-xl font-bold text-white">Paliers de commission vendeurs</h3>
          </div>
          <button
            onClick={addTier}
            className="btn-secondary py-2 px-4 text-sm flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter un palier</span>
          </button>
        </div>
        
        <p className="text-[#6B6B6E] mb-4 text-sm">
          Commission fixe en euros par vente selon le nombre de ventes du mois
        </p>

        <div className="space-y-3">
          {tiers.map((tier, index) => (
            <div key={index} className="bg-[#222226] p-4 rounded-lg flex items-center space-x-4">
              <div className="flex-1 grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-[#6B6B6E] text-xs mb-1">Nom</label>
                  <input
                    type="text"
                    value={tier.name}
                    onChange={(e) => updateTier(index, 'name', e.target.value)}
                    className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[#6B6B6E] text-xs mb-1">Ventes min</label>
                  <input
                    type="number"
                    value={tier.min_sales}
                    onChange={(e) => updateTier(index, 'min_sales', e.target.value)}
                    className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[#6B6B6E] text-xs mb-1">Ventes max</label>
                  <input
                    type="number"
                    value={tier.max_sales}
                    onChange={(e) => updateTier(index, 'max_sales', e.target.value)}
                    className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[#6B6B6E] text-xs mb-1">Commission (€)</label>
                  <input
                    type="number"
                    value={tier.commission}
                    onChange={(e) => updateTier(index, 'commission', e.target.value)}
                    className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-[#00FFFF] font-bold text-sm"
                  />
                </div>
              </div>
              <button
                onClick={() => removeTier(index)}
                disabled={tiers.length <= 1}
                className="text-[#FF3B5C] hover:text-[#FF2244] disabled:opacity-30 disabled:cursor-not-allowed p-2"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>

        {/* Preview */}
        <div className="mt-6 bg-[#222226] p-4 rounded-lg">
          <h4 className="text-[#B8B8BA] text-sm font-semibold mb-3">Aperçu des paliers</h4>
          <div className="flex flex-wrap gap-2">
            {tiers.map((tier, index) => (
              <div key={index} className="bg-[#1A1A1D] px-4 py-2 rounded-lg">
                <span className="text-[#B8B8BA] text-sm">{tier.min_sales}-{tier.max_sales} ventes/mois : </span>
                <span className="text-[#00FFFF] font-bold">{tier.commission}€</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Referral Commissions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#1A1A1D] p-6 rounded-lg metallic-border"
      >
        <div className="flex items-center space-x-3 mb-6">
          <Users className="h-6 w-6 text-[#00FF88]" />
          <h3 className="text-xl font-bold text-white">Commissions de parrainage</h3>
        </div>
        
        <p className="text-[#6B6B6E] mb-4 text-sm">
          Commission en euros versée aux parrains lors d'une vente par un filleul
        </p>

        <div className="grid grid-cols-2 gap-6">
          <div className="bg-[#222226] p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 bg-[#00FF88]/20 rounded-full flex items-center justify-center">
                <span className="text-[#00FF88] font-bold text-sm">N+1</span>
              </div>
              <div>
                <h4 className="text-white font-semibold">Parrainage direct</h4>
                <p className="text-[#6B6B6E] text-xs">Commission quand votre filleul fait une vente</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={referral.n1_commission}
                onChange={(e) => setReferral({...referral, n1_commission: parseFloat(e.target.value) || 0})}
                className="w-24 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-[#00FF88] font-bold text-lg"
              />
              <span className="text-[#B8B8BA]">€ par vente</span>
            </div>
          </div>

          <div className="bg-[#222226] p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 bg-[#00FFFF]/20 rounded-full flex items-center justify-center">
                <span className="text-[#00FFFF] font-bold text-sm">N+2</span>
              </div>
              <div>
                <h4 className="text-white font-semibold">Parrainage indirect</h4>
                <p className="text-[#6B6B6E] text-xs">Commission quand le filleul de votre filleul fait une vente</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={referral.n2_commission}
                onChange={(e) => setReferral({...referral, n2_commission: parseFloat(e.target.value) || 0})}
                className="w-24 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-[#00FFFF] font-bold text-lg"
              />
              <span className="text-[#B8B8BA]">€ par vente</span>
            </div>
          </div>
        </div>

        {/* Explanation */}
        <div className="mt-4 bg-[#FFB800]/10 border border-[#FFB800]/30 p-4 rounded-lg">
          <p className="text-[#FFB800] text-sm">
            <strong>Exemple :</strong> Si un vendeur A parraine B, et B parraine C :
          </p>
          <ul className="text-[#B8B8BA] text-sm mt-2 space-y-1">
            <li>• Quand B fait une vente → A reçoit {referral.n1_commission}€ (N+1)</li>
            <li>• Quand C fait une vente → B reçoit {referral.n1_commission}€ (N+1) et A reçoit {referral.n2_commission}€ (N+2)</li>
          </ul>
        </div>
      </motion.div>

      {/* Client Referral Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-[#1A1A1D] p-6 rounded-lg metallic-border"
      >
        <div className="flex items-center space-x-3 mb-6">
          <Gift className="h-6 w-6 text-[#FF6B9D]" />
          <h3 className="text-xl font-bold text-white">Parrainage client</h3>
        </div>
        
        <p className="text-[#6B6B6E] mb-4 text-sm">
          Paramètres quand un client parraine un autre client
        </p>

        <div className="grid grid-cols-2 gap-6">
          <div className="bg-[#222226] p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 bg-[#00FFFF]/20 rounded-full flex items-center justify-center">
                <Euro className="h-4 w-4 text-[#00FFFF]" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Commission vendeur</h4>
                <p className="text-[#6B6B6E] text-xs">Quand son client parraine quelqu'un</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={clientReferral.vendor_commission_on_client_referral}
                onChange={(e) => setClientReferral({...clientReferral, vendor_commission_on_client_referral: parseFloat(e.target.value) || 0})}
                className="w-24 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-[#00FFFF] font-bold text-lg"
              />
              <span className="text-[#B8B8BA]">€ par parrainage</span>
            </div>
          </div>

          <div className="bg-[#222226] p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 bg-[#FF6B9D]/20 rounded-full flex items-center justify-center">
                <Percent className="h-4 w-4 text-[#FF6B9D]" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Réduction parrainé</h4>
                <p className="text-[#6B6B6E] text-xs">Réduction pour le nouveau client parrainé</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={clientReferral.referred_client_discount}
                onChange={(e) => setClientReferral({...clientReferral, referred_client_discount: parseFloat(e.target.value) || 0})}
                className="w-24 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-[#FF6B9D] font-bold text-lg"
              />
              <span className="text-[#B8B8BA]">€ de réduction</span>
            </div>
          </div>

          {/* Client Referrer Bonus */}
          <div className="flex items-center justify-between p-4 bg-[#222226] rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-[#00FF88]/20 rounded-full flex items-center justify-center">
                <Gift className="h-4 w-4 text-[#00FF88]" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Bonus parrain (client)</h4>
                <p className="text-[#6B6B6E] text-xs">Avoir crédité au client parrain</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={clientReferral.client_referrer_bonus}
                onChange={(e) => setClientReferral({...clientReferral, client_referrer_bonus: parseFloat(e.target.value) || 0})}
                className="w-24 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-[#00FF88] font-bold text-lg"
              />
              <span className="text-[#B8B8BA]">€ de crédit</span>
            </div>
          </div>
        </div>

        {/* Explanation */}
        <div className="mt-4 bg-[#FF6B9D]/10 border border-[#FF6B9D]/30 p-4 rounded-lg">
          <p className="text-[#FF6B9D] text-sm">
            <strong>Exemple :</strong> Un client A parraine un client B :
          </p>
          <ul className="text-[#B8B8BA] text-sm mt-2 space-y-1">
            <li>• <strong>Le client A</strong> (parrain) reçoit <span className="text-[#00FF88] font-bold">{clientReferral.client_referrer_bonus}€</span> de crédit</li>
            <li>• <strong>Le client B</strong> (parrainé) bénéficie d'une réduction de <span className="text-[#FF6B9D] font-bold">{clientReferral.referred_client_discount}€</span></li>
            <li>• <strong>Le vendeur</strong> qui a vendu à A reçoit <span className="text-[#00FFFF] font-bold">{clientReferral.vendor_commission_on_client_referral}€</span> de commission</li>
          </ul>
        </div>
      </motion.div>

      {/* Monthly Tier Recalculation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-[#1A1A1D] p-6 rounded-lg metallic-border"
      >
        <div className="flex items-center space-x-3 mb-6">
          <Calendar className="h-6 w-6 text-[#FFB800]" />
          <h3 className="text-xl font-bold text-white">Recalcul mensuel des paliers (M-1)</h3>
        </div>
        
        <p className="text-[#6B6B6E] mb-4 text-sm">
          Recalcule les commissions de tous les vendeurs en fonction de leurs ventes du mois précédent.
          Ce calcul est normalement automatisé au début de chaque mois.
        </p>

        <div className="flex items-center space-x-4">
          <button
            onClick={handleRecalculateTiers}
            disabled={recalculating}
            className="btn-secondary py-3 px-6 flex items-center space-x-2 disabled:opacity-50"
          >
            {recalculating ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Recalcul en cours...</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-5 w-5" />
                <span>Recalculer les paliers</span>
              </>
            )}
          </button>
        </div>

        {/* Recalculate Result */}
        {recalculateResult && (
          <div className="mt-6 bg-[#222226] p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <CheckCircle className="h-5 w-5 text-[#00FF88]" />
              <span className="text-white font-semibold">{recalculateResult.message}</span>
            </div>
            <p className="text-[#6B6B6E] text-sm mb-4">Période analysée : {recalculateResult.period}</p>
            
            {recalculateResult.vendors && recalculateResult.vendors.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#6B6B6E] border-b border-[#3A3A3D]">
                      <th className="py-2 text-left">Vendeur</th>
                      <th className="py-2 text-center">Ventes M-1</th>
                      <th className="py-2 text-center">Ancien palier</th>
                      <th className="py-2 text-center">Nouveau palier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recalculateResult.vendors.map((vendor, index) => (
                      <tr key={index} className="border-b border-[#3A3A3D]/50">
                        <td className="py-2 text-white">{vendor.full_name || vendor.email}</td>
                        <td className="py-2 text-center text-[#00FFFF]">{vendor.sales_m1}</td>
                        <td className="py-2 text-center text-[#B8B8BA]">{vendor.old_commission}€</td>
                        <td className="py-2 text-center">
                          <span className={`font-bold ${vendor.new_commission > vendor.old_commission ? 'text-[#00FF88]' : vendor.new_commission < vendor.old_commission ? 'text-[#FF3B5C]' : 'text-white'}`}>
                            {vendor.new_commission}€
                          </span>
                          <span className="text-[#6B6B6E] ml-1">({vendor.tier})</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary py-3 px-8 flex items-center space-x-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Enregistrement...</span>
            </>
          ) : (
            <>
              <Save className="h-5 w-5" />
              <span>Enregistrer les paramètres</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CommissionSettings;
